import React, { useState } from 'react';
import { FormattedMessageWrappedInSpan } from '../components/misc';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import Slider from 'react-slick';

import EmailSuggestionTooltip from '../components/misc/EmailSuggestionTooltip';
import useEmailSpellChecking from '../hooks/useEmailSpellChecking';
import isEmailProper from '../utils/isEmailProper';
import environment from '../environment';
import { SubscribeToNewsletterMutation } from '../mutations';
import { getAppLanguage } from '../intl/languages';
import { trackEvent } from '../utils/ga-tracking';

import {
  Tooltip,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core';
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';

import img_1 from '../static/images/newletterPage/newsletter-1.jpg';
import img_2 from '../static/images/newletterPage/newsletter-2.jpg';
import img_3 from '../static/images/newletterPage/newsletter-3.jpg';
import img_4 from '../static/images/newletterPage/newsletter-4.jpg';
import img_5 from '../static/images/newletterPage/newsletter-5.jpg';
import img_6 from '../static/images/newletterPage/newsletter-6.jpg';
import img_7 from '../static/images/newletterPage/newsletter-7.jpg';
import img_8 from '../static/images/newletterPage/newsletter-8.jpg';

const NewsletterPage = () => {
  const [userEmail, setUserEmail] = useState('');
  const [consentToSubscribe, setConsentToSubscribe] = useState(false);
  const [userEmailError, setUserEmailError] = useState(null);
  const [checkboxError, setCheckboxError] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [newsletterResult, setNewsletterResult] = useState();

  const intl = useIntl();

  let suggestedEmail = useEmailSpellChecking(userEmail);

  const required_error = 'required';
  const inproper_input_error = 'inproper';
  const newsletter_status_pending = 'pending';
  const newsletter_status_subscribed = 'subscribed';
  const newsletter_status_error = 'error';

  const checkFields = () => {
    if (!userEmail) {
      setUserEmailError(required_error);
      return true;
    }
    if (!isEmailProper(userEmail)) {
      setUserEmailError(inproper_input_error);
      return true;
    }
    if (!consentToSubscribe) {
      setCheckboxError(true);
      return true;
    }
  };

  const handleSubscribeToNewsletter = () => {
    const errors = checkFields();
    if (!errors) {
      subscribeToRaceletter();
    }
  };

  const onCheckboxChange = () => {
    setConsentToSubscribe((prevVal) => !prevVal);
    if (checkboxError) {
      setCheckboxError(false);
    }
  };

  const onUserEmailChange = (e) => {
    setUserEmail(e.target.value);
    if (userEmailError) {
      setUserEmailError(null);
    }
  };

  const renderHelperText = () => {
    if (userEmailError === required_error) {
      return intl.formatMessage({
        id: 'newsletterPage.form.emailRequiredError',
        defaultMessage: 'This field is required',
      });
    }
    if (userEmailError === inproper_input_error) {
      return intl.formatMessage({
        id: 'newsletterPage.form.emailInproperError',
        defaultMessage: 'Please enter a valid email address',
      });
    }
  };

  const renderButtonText = () => {
    if (isPending) {
      return <CircularProgress size={20} />;
    }
    if (newsletterResult === newsletter_status_pending) {
      return (
        <FormattedMessageWrappedInSpan
          id="newsletterPage.form.button.checkEmail"
          defaultMessage="Check email"
        />
      );
    }
    if (newsletterResult === newsletter_status_subscribed) {
      return (
        <FormattedMessageWrappedInSpan
          id="newsletterPage.form.button.youAreAlreadySubscribed"
          defaultMessage="You are already in"
        />
      );
    }
    if (newsletterResult === newsletter_status_error) {
      return (
        <FormattedMessageWrappedInSpan
          id="newsletterPage.form.button.error"
          defaultMessage="Error. Try again"
        />
      );
    }
    return (
      <FormattedMessageWrappedInSpan
        id="newsletterPage.form.button.mainAction"
        defaultMessage="Subscribe"
      />
    );
  };

  const subscribeToRaceletter = () => {
    setIsPending(true);
    SubscribeToNewsletterMutation.commit(
      environment,
      userEmail,
      getAppLanguage(),
      (err, status) => {
        if (err) {
          setNewsletterResult(newsletter_status_error);
        } else {
          setNewsletterResult(status);
          trackEvent('Marketing', 'Event Reminder', 'sign up for newsletter');
        }
        setIsPending(false);
      }
    );
  };

  // carousel
  const images = [img_1, img_2, img_3, img_4, img_5, img_6, img_7];

  return (
    <div className="newsletter-page">
      <h1 className="newsletter-page_heading">
        <FormattedMessageWrappedInSpan
          id="newsletterPage.heading"
          defaultMessage="Sportograf raceletter"
        />
      </h1>
      <div className="container-960">
        <div className="newsletter-page_images">
          <Slider
            autoplay={true}
            arrows={false}
            dots={false}
            draggable={false}
            swipe={false}
            pauseOnHover={false}
            touchMove={false}
            className="newsletter-page_images_carousel"
          >
            {images.map((src, index) => (
              <img src={src} alt="" key={index} />
            ))}
          </Slider>
        </div>
        <div className="newsletter-page_form-container">
          <form className="newsletter-page_form" onSubmit={handleSubscribeToNewsletter}>
            <div className="newsletter-page_form-heading">
              <FormattedMessageWrappedInSpan
                id="newsletterPage.form.heading"
                defaultMessage="Join the Sportograf Raceletter"
              />
            </div>
            {(!newsletterResult || newsletterResult === newsletter_status_error) && (
              <div className="newsletter-page_form-texts mb-20">
                <div>
                  <FormattedMessageWrappedInSpan
                    id="newsletterPage.form.message"
                    defaultMessage="Get updates on exciting events, insights from the world of sports, and special offers from our partners. Ready to dive in?"
                  />
                </div>
                <div className="newsletter-page_form-actions">
                  <EmailSuggestionTooltip
                    emailSuggestion={suggestedEmail}
                    onAcceptSuggestion={() => {
                      setUserEmail(suggestedEmail);
                    }}
                  >
                    <TextField
                      autoComplete="off"
                      placeholder={intl.formatMessage({
                        id: 'newsletterPage.form.emailPlaceholder',
                        defaultMessage: 'Enter your email',
                      })}
                      type="text"
                      fullWidth
                      onChange={(e) => onUserEmailChange(e)}
                      value={userEmail}
                      classes={{ underline: 'newsletter-page_field' }}
                      className="newsletter-page_field"
                      error={userEmailError}
                      helperText={userEmailError && renderHelperText()}
                      InputProps={{
                        endAdornment: !userEmail && (
                          <InputAdornment position="end">
                            <CreateOutlinedIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </EmailSuggestionTooltip>
                  <div>
                    <Tooltip
                      title={
                        <FormattedMessageWrappedInSpan
                          id="newsletterPage.form.checkboxErrorTooltip"
                          defaultMessage="This consent is required."
                        />
                      }
                      open={checkboxError}
                      arrow
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            className={`newsletter-checkbox ${
                              checkboxError ? 'newsletter-checkbox-error' : ''
                            }`}
                            color="primary"
                            value={consentToSubscribe}
                            onChange={onCheckboxChange}
                          />
                        }
                        label={
                          <FormattedMessageWrappedInSpan
                            id="newsletterPage.form.consent"
                            defaultMessage="I agree to receive occasional emails from Sportograf. I can unsubscribe at any time by sending an email to support@sportograf.com."
                          />
                        }
                        classes={{
                          label: `newsletter-checkbox-label ${
                            checkboxError ? 'newsletter-checkbox-error' : ''
                          }`,
                        }}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}
            {newsletterResult === newsletter_status_pending && (
              <div className="newsletter-page_form-texts mb-20">
                <FormattedMessageWrappedInSpan
                  id="newsletterPage.pendingStateDescription"
                  defaultMessage="Thank you for subscribing to our Newsletter. We send you an email, where you can confirm your email address."
                />
              </div>
            )}
            {newsletterResult === newsletter_status_subscribed && (
              <div className="newsletter-page_form-texts">
                <FormattedMessageWrappedInSpan
                  id="newsletterPage.newsletter.subscribedStateDescription"
                  defaultMessage="You are already registered for our newsletter."
                />
              </div>
            )}
          </form>
          <button
            onClick={handleSubscribeToNewsletter}
            className={classNames(
              ' newsletter-page_form-button btn-default warning-btn full-width btn-in-form'
            )}
            disabled={
              isPending ||
              newsletterResult === newsletter_status_pending ||
              newsletterResult === newsletter_status_subscribed
            }
          >
            {renderButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPage;